import { FormEvent, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button, Tile, TileContent, TileHeader, TileTitle } from '../../components/ui';

type InviteStatus = 'valid' | 'expired' | 'used' | 'not_found' | 'disabled' | 'loading';

export default function ParentInvitePublic() {
  const { token = '' } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const inviteQuery = useQuery(
    ['parent-invite', token],
    async () => {
      const response = await api.get(`/auth/parent-invite/${encodeURIComponent(token)}`);
      return response.data?.data;
    },
    {
      retry: false
    }
  );

  const acceptMutation = useMutation(async () => {
    const response = await api.post(`/auth/parent-invite/${encodeURIComponent(token)}/accept`, {
      password,
      first_name: firstName || null,
      last_name: lastName || null
    });
    return response.data;
  });

  let status: InviteStatus = 'loading';
  if (inviteQuery.isSuccess) status = 'valid';
  if (inviteQuery.isError) {
    const code = (inviteQuery.error as any)?.response?.data?.error;
    if (code === 'PARENT_PORTAL_DISABLED') status = 'disabled';
    else if (code === 'INVITE_EXPIRED') status = 'expired';
    else if (code === 'INVITE_ALREADY_USED') status = 'used';
    else status = 'not_found';
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 6) return;
    if (password !== confirmPassword) return;
    try {
      const result = await acceptMutation.mutateAsync();
      const loggedInUser = result?.data?.user;
      if (loggedInUser) {
        sessionStorage.setItem('user', JSON.stringify(loggedInUser));
        localStorage.setItem('auth:sync', String(Date.now()));
      }
      window.location.assign('/eltern/nachrichten');
    } catch {
      // error shown below
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-xl items-center px-4">
      <Tile className="w-full">
        <TileHeader>
          <TileTitle>Einladung zum Elternportal</TileTitle>
        </TileHeader>
        <TileContent>
          {status === 'loading' ? <p className="text-sm text-gray-600 dark:text-gray-300">Einladung wird geprüft...</p> : null}
          {status === 'expired' ? <p className="text-sm text-red-700">Der Einladungslink ist abgelaufen.</p> : null}
          {status === 'used' ? <p className="text-sm text-amber-700">Diese Einladung wurde bereits verwendet.</p> : null}
          {status === 'not_found' ? <p className="text-sm text-red-700">Einladung nicht gefunden.</p> : null}
          {status === 'disabled' ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Das Elternportal ist derzeit nicht verfügbar. Bitte wenden Sie sich an die Schule.
            </p>
          ) : null}

          {status === 'valid' ? (
            <form className="space-y-3" onSubmit={submit}>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Konto für: <strong>{inviteQuery.data?.email}</strong>
              </p>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Vorname (optional)
                <input
                  className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Nachname (optional)
                <input
                  className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Passwort
                <input
                  type="password"
                  className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={6}
                  required
                />
              </label>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Passwort wiederholen
                <input
                  type="password"
                  className="input-field mt-1 min-h-[2.25rem] py-2 text-sm"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={6}
                  required
                />
              </label>
              {password && confirmPassword && password !== confirmPassword ? (
                <p className="text-sm text-red-700">Passwörter stimmen nicht überein.</p>
              ) : null}
              {acceptMutation.isError ? (
                <p className="text-sm text-red-700">
                  {(acceptMutation.error as any)?.response?.data?.message || 'Passwort konnte nicht gespeichert werden.'}
                </p>
              ) : null}
              <Button type="submit" disabled={acceptMutation.isLoading || password !== confirmPassword || password.length < 6}>
                {acceptMutation.isLoading ? 'Speichern...' : 'Passwort setzen und anmelden'}
              </Button>
            </form>
          ) : null}
        </TileContent>
      </Tile>
    </div>
  );
}
